import { useState, forwardRef, useImperativeHandle } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "@/locales";

// 用户数据类型定义
interface User {
  id: number;
  nickname: string;
  email: string;
  role: "USER" | "ADMIN" | "ROOT";
  joinDate: string;
  state: "OK" | "INACTIVE" | "SUSPENDED";
}

// 组件属性定义
interface EditUserDialogProps {
  // 修改成功后的回调
  onSuccess?: (response: any) => void;
}

// 暴露给父组件的方法类型
interface DialogRef {
  open: (user: User) => void;
}

export const EditUserDialog = forwardRef<DialogRef, EditUserDialogProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation();

  // 表单数据类型定义
  const editUserFormSchema = z.object({
    role: z.enum(["USER", "ADMIN"], { required_error: t("admin.validation.roleRequired") }),
  });

  type EditUserFormValues = z.infer<typeof editUserFormSchema>;

  // 控制弹窗显示状态
  const [isOpen, setIsOpen] = useState(false);
  // 控制提交加载状态
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 错误信息
  const [errorMessage, setErrorMessage] = useState("");
  // 当前编辑的用户
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // 表单配置
  const form = useForm<EditUserFormValues>({
    resolver: zodResolver(editUserFormSchema),
    defaultValues: {
      role: "USER",
    },
  });

  // 暴露open方法给父组件
  useImperativeHandle(ref, () => ({
    open: (user: User) => {
      setCurrentUser(user);
      setIsOpen(true);
      // 填充表单数据
      form.reset({
        role: user.role === "ROOT" ? "ADMIN" : user.role, // ROOT角色显示为ADMIN
      });
      setErrorMessage(""); // 清空错误信息
    },
  }));

  // 处理表单提交
  const onSubmit = async (data: EditUserFormValues) => {
    if (!currentUser) return;

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      // 调用修改用户角色API
      const formData = new URLSearchParams();
      formData.append("uid", currentUser.uid);
      formData.append("role", data.role);

      const response: any = await api.post("/v1/admin/team/user/role", formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      if (response.success !== false) {
        // 接口成功 - 关闭弹窗并触发回调
        setIsOpen(false);
        onSuccess?.(response);
      } else {
        // 接口返回错误信息
        setErrorMessage(response.message || t("admin.editUser.editFailed"));
      }
    } catch (error: any) {
      // 捕获网络等异常错误
      console.error("修改用户角色失败:", error);
      setErrorMessage(error?.data?.msg || t("common.networkError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  // 关闭弹窗
  const handleClose = () => {
    setIsOpen(false);
    setCurrentUser(null);
    form.reset();
    setErrorMessage("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("admin.editUser.editUserRole")}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* 用户信息展示 */}
            <div className="space-y-3   ">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("admin.nickname")}</label>
                <p className="text-sm text-gray-900 dark:text-gray-100">{currentUser?.nickname}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("admin.email")}</label>
                <p className="text-sm text-gray-900 dark:text-gray-100">{currentUser?.email}</p>
              </div>
            </div>

            {/* 角色类型字段 */}
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("admin.editUser.roleType")}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isSubmitting}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t("admin.editUser.selectRole")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="USER">{t("admin.editUser.user")}</SelectItem>
                      <SelectItem value="ADMIN">{t("admin.editUser.admin")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* 错误信息显示 */}
            {errorMessage && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">{errorMessage}</div>}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting} className={"bg-blue-500 hover:bg-blue-600 dark:text-[#fff]"}>
                {isSubmitting ? t("common.saving") : t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});

EditUserDialog.displayName = "EditUserDialog";
